/* eslint-disable react/prop-types */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Stack,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Dashboard,
  Group,
  Work,
  Description,
  Settings,
  Star,
  ArrowForward,
  Search,
  Videocam,
  PersonAdd,
  AccessTime,
  TrendingDown,
  EmojiEvents,
  PeopleAlt,
  Person,
  CheckCircle,
  Business,
  Storage,
  Assignment,
  Notifications,
  FilterList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import ScrollAnimation from '../utils/ScrollAnimation';
import { getPlanData } from '../service/planService';
import BotIconImg from '../../assets/icons/merubot-icon.png';

const HeroDashboardMockup = () => (
  <Paper
    elevation={0}
    sx={{
      position: 'relative',
      width: '100%',
      maxWidth: { xs: 400, md: 540, lg: 600 },
      mx: 'auto',
      borderRadius: '16px',
      overflow: 'hidden',
      border: '1px solid rgba(226, 232, 240, 0.8)',
      bgcolor: '#F4F7F9',
      boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.15), inset 0 1px 1px rgba(255,255,255,0.8)',
      animation: 'float 6s ease-in-out infinite',
      '@keyframes float': {
        '0%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-12px)' },
        '100%': { transform: 'translateY(0px)' },
      },
    }}
  >
    {/* Mac Window Header */}
    <Box
      sx={{
        height: 24,
        borderBottom: '1px solid rgba(15, 23, 42, 0.05)',
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        bgcolor: '#FFFFFF',
      }}
    >
      <Stack direction="row" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF5F56' }} />
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FFBD2E' }} />
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#27C93F' }} />
      </Stack>
    </Box>

    <Box sx={{ display: 'flex', height: { xs: 'auto', md: 340 }, minHeight: { xs: 0, md: 340 } }}>
      {/* Left Sidebar */}
      <Box
        sx={{
          width: 85,
          bgcolor: '#FFFFFF',
          borderRight: '1px solid #E2E8F0',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Box sx={{ px: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 900,
              color: 'primary.main',
              letterSpacing: '-0.2px',
              pl: 0.5,
            }}
          >
            MeruDesk
          </Typography>
        </Box>

        <Stack spacing={0.5} sx={{ px: 1 }}>
          {[
            { icon: <PeopleAlt sx={{ fontSize: 12 }} />, label: 'Talent Acq...' },
            { icon: <Person sx={{ fontSize: 12 }} />, label: 'Human Res...' },
            { icon: <Group sx={{ fontSize: 12 }} />, label: 'User Mgmt' },
            { icon: <CheckCircle sx={{ fontSize: 12 }} />, label: 'Approvals', badge: '12' },
            { icon: <Settings sx={{ fontSize: 12 }} />, label: 'Preferences' },
            { icon: <Work sx={{ fontSize: 12 }} />, label: 'Projects' },
            { icon: <Business sx={{ fontSize: 12 }} />, label: 'Clients' },
            { icon: <Storage sx={{ fontSize: 12 }} />, label: 'Master Data' },
            { icon: <Assignment sx={{ fontSize: 12 }} />, label: 'My Requis...' },
          ].map((item, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                borderRadius: '4px',
                '&:hover': { bgcolor: '#F1F5F9' },
                cursor: 'pointer',
              }}
            >
              <Box sx={{ color: 'text.secondary', mr: 0.75, display: 'flex' }}>{item.icon}</Box>
              <Typography
                sx={{
                  fontSize: '0.45rem',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                }}
              >
                {item.label}
              </Typography>
              {item.badge && (
                <Box
                  sx={{
                    bgcolor: '#FEF3C7',
                    color: '#D97706',
                    fontSize: '0.4rem',
                    px: 0.5,
                    borderRadius: '4px',
                    fontWeight: 700,
                  }}
                >
                  {item.badge}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Navbar */}
        <Box
          sx={{
            height: { xs: 40, md: 32 },
            flexShrink: 0,
            bgcolor: '#FFFFFF',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            px: { xs: 1.25, md: 1.5 },
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#F1F5F9',
              px: 1,
              py: 0.5,
              borderRadius: '6px',
              width: { xs: 150, md: 120 },
            }}
          >
            <Search sx={{ fontSize: { xs: 14, md: 10 }, color: 'text.secondary', mr: 0.5 }} />
            <Typography sx={{ fontSize: { xs: '0.62rem', md: '0.45rem' }, color: 'text.secondary' }}>
              Search Candidates...
            </Typography>
          </Box>
          <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center">
            <Notifications sx={{ fontSize: { xs: 16, md: 12 }, color: '#F59E0B' }} />
            <Settings sx={{ fontSize: { xs: 16, md: 12 }, color: 'text.secondary' }} />
            <Avatar
              sx={{
                width: { xs: 22, md: 16 },
                height: { xs: 22, md: 16 },
                bgcolor: '#8B5CF6',
                fontSize: { xs: '0.65rem', md: '0.5rem' },
                fontWeight: 700,
              }}
            >
              A
            </Avatar>
          </Stack>
        </Box>

        {/* Dashboard Body */}
        <Box
          sx={{
            p: { xs: 1.5, md: 1.5 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.25, md: 1.5 },
            minWidth: 0,
          }}
        >
          {/* Greeting & Filters */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '0.82rem', md: '0.7rem' },
                  fontWeight: 800,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                Good Morning, <span style={{ color: '#10B981' }}>Alex</span> 👋
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.62rem', md: '0.45rem' },
                  color: 'text.secondary',
                  mt: 0.25,
                }}
              >
                Start your day with focus — great hiring decisions begin today
              </Typography>
            </Box>
            <Box
              sx={{
                border: '1px solid #E2E8F0',
                bgcolor: '#fff',
                px: { xs: 0.75, md: 1 },
                py: 0.5,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <FilterList sx={{ fontSize: { xs: 12, md: 8 }, color: 'text.secondary' }} />
              <Typography
                sx={{
                  fontSize: { xs: '0.58rem', md: '0.45rem' },
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                This Month
              </Typography>
            </Box>
          </Box>

          {/* KPI Cards Row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
              gap: { xs: 1, md: 1 },
            }}
          >
            {[
              { label: 'Employees', value: '142', color: '#10B981' },
              { label: 'Candidates', value: '85', color: '#3B82F6' },
              { label: 'Open Roles', value: '12', color: '#EF4444' },
              { label: 'Active Projects', value: '24', color: '#8B5CF6' },
              { label: 'Clients', value: '18', color: '#F59E0B' },
            ].map((kpi, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  flex: 1,
                  p: { xs: 1, md: 0.75 },
                  borderRadius: '10px',
                  border: '1px solid #E2E8F0',
                  borderLeft: `3px solid ${kpi.color}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.62rem', md: '0.45rem' },
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  {kpi.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.9rem', md: '0.75rem' },
                    fontWeight: 800,
                    color: 'text.primary',
                  }}
                >
                  {kpi.value}
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Charts Row */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flex={1}>
            {/* Project Status Donut Chart */}
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 1,
                borderRadius: '12px',
                border: '1px solid #E2E8F0',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.68rem', md: '0.5rem' },
                  fontWeight: 800,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Project Status
              </Typography>
              <Box
                sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Box
                  sx={{
                    width: { xs: 84, md: 70 },
                    height: { xs: 84, md: 70 },
                    borderRadius: '50%',
                    background: 'conic-gradient(#8B5CF6 0% 55%, #F59E0B 55% 80%, #10B981 80% 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 60, md: 50 },
                      height: { xs: 60, md: 50 },
                      bgcolor: '#fff',
                      borderRadius: '50%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '0.78rem', md: '0.65rem' },
                        fontWeight: 800,
                        color: 'text.primary',
                        lineHeight: 1,
                      }}
                    >
                      24
                    </Typography>
                    <Typography
                      sx={{ fontSize: { xs: '0.5rem', md: '0.35rem' }, color: 'text.secondary' }}
                    >
                      Total Projects
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Recruitment Pipeline */}
            <Paper
              elevation={0}
              sx={{ flex: 1, p: 1, borderRadius: '12px', border: '1px solid #E2E8F0' }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.68rem', md: '0.5rem' },
                  fontWeight: 800,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Recruitment Pipeline
              </Typography>
              <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                <Box
                  sx={{
                    width: '100%',
                    py: 0.5,
                    bgcolor: '#0F62FE',
                    color: '#fff',
                    textAlign: 'center',
                    borderRadius: '4px',
                    fontSize: { xs: '0.62rem', md: '0.45rem' },
                    fontWeight: 700,
                  }}
                >
                  Total Candidates: 85
                </Box>
                <Box
                  sx={{
                    width: '80%',
                    py: 0.5,
                    bgcolor: '#3B82F6',
                    color: '#fff',
                    textAlign: 'center',
                    borderRadius: '4px',
                    fontSize: { xs: '0.62rem', md: '0.45rem' },
                    fontWeight: 700,
                  }}
                >
                  Interviewed: 32
                </Box>
                <Box
                  sx={{
                    width: '60%',
                    py: 0.5,
                    bgcolor: '#10B981',
                    color: '#fff',
                    textAlign: 'center',
                    borderRadius: '4px',
                    fontSize: { xs: '0.62rem', md: '0.45rem' },
                    fontWeight: 700,
                  }}
                >
                  Selected: 14
                </Box>
                <Box
                  sx={{
                    width: '40%',
                    py: 0.5,
                    bgcolor: '#F59E0B',
                    color: '#fff',
                    textAlign: 'center',
                    borderRadius: '4px',
                    fontSize: { xs: '0.62rem', md: '0.45rem' },
                    fontWeight: 700,
                  }}
                >
                  Offered: 5
                </Box>
              </Stack>
            </Paper>

            {/* Employee by Project */}
            <Paper
              elevation={0}
              sx={{ flex: 1, p: 1, borderRadius: '12px', border: '1px solid #E2E8F0' }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.68rem', md: '0.5rem' },
                  fontWeight: 800,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Headcount Allocation
              </Typography>
              <Stack spacing={0.75}>
                {[
                  { name: 'Nexus UI', width: '85%', color: '#3B82F6' },
                  { name: 'CloudSync', width: '65%', color: '#10B981' },
                  { name: 'FinTech App', width: '45%', color: '#F59E0B' },
                  { name: 'Internal Ops', width: '30%', color: '#8B5CF6' },
                  { name: 'On Bench', width: '15%', color: '#EF4444' },
                ].map((bar, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.56rem', md: '0.4rem' },
                        color: 'text.secondary',
                        width: { xs: 70, md: 35 },
                      }}
                    >
                      {bar.name}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: { xs: 8, md: 6 },
                        bgcolor: '#F1F5F9',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: bar.width,
                          height: '100%',
                          bgcolor: bar.color,
                          borderRadius: '4px',
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </Box>
  </Paper>
);
export const Hero = ({ onStartFreeTrial, onNavigateToSection }) => {
  const navigate = useNavigate();

  const handleStartFreeTrial = () => {
    const premiumPlan = getPlanData().find((p) => p.id === 'premium');

    navigate('/onboard', {
      state: {
        selectedPlan: {
          id: premiumPlan.id,
          name: premiumPlan.name,
          desc: premiumPlan.desc,

          exactPlanId: premiumPlan.idAnnually,

          finalPrice: premiumPlan.priceAnnually,

          includedFeatures: premiumPlan.includedFeatures || [],

          excludedFeatures: premiumPlan.excludedFeatures || [],

          currency: 'INR',
          currencySymbol: '₹',

          isAnnual: true,
          isTrial: true,
        },
      },
    });
  };
  const handleBookDemo = () => {
    // 2. Navigate with the Yearly configuration
    navigate('/book-demo');
  };
  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: { xs: '96px', md: '60px' },
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: { xs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              flex: 1,
              zIndex: 10,
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <ScrollAnimation direction="right">
              <Box sx={{ maxWidth: { xs: 440, md: 450, lg: 500 } }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    display: 'inline-flex',
                    maxWidth: '100%',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 0.5 },
                    borderRadius: '16px',
                    bgcolor: 'rgba(32,101,209,0.08)',
                    border: 1,
                    borderColor: 'rgba(32,101,209,0.16)',
                    mb: { xs: 3, md: 2.5 },
                  }}
                >
                  <Star sx={{ fontSize: 14, color: 'primary.main' }} />
                  <Typography
                    variant="body2"
                    fontSize={{ xs: '0.72rem', sm: '0.75rem' }}
                    fontWeight={700}
                    color="primary.main"
                    sx={{ lineHeight: 1.4 }}
                  >
                    The Next-Gen ATS for Smarter Hiring
                  </Typography>
                </Stack>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                    mb: 2,
                    color: 'text.primary',
                    letterSpacing: '-1px',
                  }}
                >
                  Unify your entire <br />
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(to right, #2065D1, #7635dc)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    hiring lifecycle.
                  </Box>
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, fontWeight: 400, lineHeight: 1.6, fontSize: '0.95rem' }}
                >
                  Streamline your hiring from sourcing and tracking to onboarding in one intuitive,
                  beautifully designed workspace. Automate workflows, collaborate seamlessly, and
                  hire top candidates faster all in one beautifully designed workspace.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  mb={4}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                    onClick={handleStartFreeTrial}
                    sx={{ minHeight: 48, px: 2.5 }}
                  >
                    Start Free Trial
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      minHeight: 48,
                      px: 2.5,
                      width: { xs: '100%', sm: 'auto' },
                      color: 'text.primary',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.04)', borderColor: 'divider' },
                    }}
                    onClick={handleBookDemo}
                  >
                    Book Demo
                  </Button>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    No credit card required • 14-day free trial
                  </Typography>
                </Stack>
                <Box
                  component="a"
                  href="https://www.producthunt.com/posts/merudesk"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'rgba(218, 85, 47, 0.05)',
                    border: '1px solid rgba(218, 85, 47, 0.2)',
                    borderRadius: '12px',
                    padding: '8px 14px',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(218, 85, 47, 0.12)',
                      borderColor: 'rgba(218, 85, 47, 0.4)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(218, 85, 47, 0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '8px',
                      background: '#da552f',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="white"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M13.604 8.4h-3.405V12h3.405c.99 0 1.8-.81 1.8-1.8 0-.99-.81-1.8-1.8-1.8z" />
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm1.604 14.4h-3.405V18H7.8V6h5.804c2.319 0 4.2 1.881 4.2 4.2 0 2.319-1.881 4.2-4.2 4.2z" />
                    </svg>
                  </Box>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, textAlign: 'left' }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: 'text.secondary',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                      }}
                    >
                      Featured on
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', lineHeight: 1 }}
                    >
                      Product Hunt
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      background: 'rgba(218,85,47,0.12)',
                      border: '1px solid rgba(218,85,47,0.2)',
                      borderRadius: '8px',
                      padding: '4px 8px',
                      ml: 1,
                    }}
                  >
                    <Typography sx={{ color: '#da552f', fontSize: 10, lineHeight: 1 }}>
                      ▲
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 800, color: '#da552f', lineHeight: 1 }}
                    >
                      08
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </ScrollAnimation>
          </Box>
          <Box sx={{ flex: 1, zIndex: 10, width: '100%' }}>
            <ScrollAnimation
              direction="left"
              delay={150}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <HeroDashboardMockup />
            </ScrollAnimation>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export const MetricsSection = () => {
  const metrics = [
    {
      value: '40%',
      label: 'Reduction in Time-to-Hire',
      icon: <AccessTime sx={{ fontSize: 24, color: '#2065D1' }} />,
    },
    {
      value: '10hrs+',
      label: 'Saved weekly per recruiter',
      icon: <TrendingDown sx={{ fontSize: 24, color: '#16a34a' }} />,
    },
    {
      value: '98%',
      label: 'AI Match Accuracy',
      icon: <EmojiEvents sx={{ fontSize: 24, color: '#7635dc' }} />,
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <ScrollAnimation direction="up">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center',
            }}
          >
            {metrics.map((metric, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 16px)', md: '1 1 0' },
                  p: 2.5,
                  textAlign: 'center',
                  borderRadius: '16px',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 20px -8px rgba(145,158,171,0.15)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{metric.icon}</Box>
                <Typography variant="h4" fontWeight={800} color="text.primary" mb={0.5}>
                  {metric.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {metric.label}
                </Typography>
              </Paper>
            ))}
          </Box>
        </ScrollAnimation>
      </Container>
    </Box>
  );
};

export const PipelineJourney = () => {
  const steps = [
    {
      num: '01',
      title: 'Source & Track',
      desc: 'Career email parsing & CRM pools.',
      icon: <Search sx={{ fontSize: 18 }} />,
    },
    {
      num: '02',
      title: 'Screen via AI',
      desc: 'MeruBot technical screening.',
      icon: <Box component="img" src={BotIconImg} sx={{ width: 18, height: 18 }} />,
    },
    {
      num: '03',
      title: 'Interview & Rank',
      desc: 'Auto-scheduling & scorecard.',
      icon: <Videocam sx={{ fontSize: 18 }} />,
    },
    {
      num: '04',
      title: 'Hire & Onboard',
      desc: 'Automated onboarding workflows.',
      icon: <PersonAdd sx={{ fontSize: 18 }} />,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.paper',
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <ScrollAnimation direction="up">
          <Box textAlign="center" mb={5}>
            <Typography
              variant="overline"
              color="primary.main"
              fontWeight={800}
              letterSpacing={1.5}
              display="block"
              mb={0.5}
            >
              How It Works
            </Typography>
            <Typography
              variant="h3"
              color="text.primary"
              mb={1}
              sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, letterSpacing: '-0.5px' }}
            >
              The complete hiring pipeline.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              position: 'relative',
              gap: { xs: 4, md: 2 },
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'absolute',
                top: '24px',
                left: '10%',
                width: '80%',
                height: '2px',
                bgcolor: 'divider',
                zIndex: 0,
              }}
            />
            {steps.map((step, idx) => (
              <Box key={idx} sx={{ flex: 1, position: 'relative', zIndex: 1, px: { md: 1 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'background.paper',
                      border: '2px solid',
                      borderColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: 'primary.main',
                      boxShadow: '0 6px 12px rgba(32,101,209,0.15)',
                      transition: 'transform 0.3s ease',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography variant="caption" fontWeight={800} color="text.secondary" mb={0.5}>
                    STEP {step.num}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={0.5}>
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </ScrollAnimation>
      </Container>
    </Box>
  );
};
